import "@/App.css";
import { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, NavLink, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import api from "@/lib/api";
import {
  LayoutDashboard, Building2, Briefcase, FileText, Send,
  History, TerminalSquare, Settings as SettingsIcon, ChefHat, Facebook, Bell,
} from "lucide-react";
import Dashboard from "@/pages/Dashboard";
import Leads from "@/pages/Leads";
import Jobs from "@/pages/Jobs";
import CVManager from "@/pages/CVManager";
import Compose from "@/pages/Compose";
import SentHistory from "@/pages/SentHistory";
import ScraperLogs from "@/pages/ScraperLogs";
import Settings from "@/pages/Settings";
import FacebookImport from "@/pages/FacebookImport";
import Alerts from "@/pages/Alerts";

const NAV = [
  { to: "/", label: "Panoramica", icon: LayoutDashboard, testid: "nav-dashboard", end: true },
  { to: "/hotel", label: "Hotel & Email", icon: Building2, testid: "nav-leads" },
  { to: "/offerte", label: "Offerte Lavoro", icon: Briefcase, testid: "nav-jobs" },
  { to: "/facebook", label: "Import Facebook", icon: Facebook, testid: "nav-facebook" },
  { to: "/avvisi", label: "Avvisi", icon: Bell, testid: "nav-alerts", badge: true },
  { to: "/cv", label: "Gestione CV", icon: FileText, testid: "nav-cv" },
  { to: "/invia", label: "Invia Candidature", icon: Send, testid: "nav-compose" },
  { to: "/storico", label: "Storico Inviate", icon: History, testid: "nav-sent" },
  { to: "/scraper", label: "Scraper & Anti-Bot", icon: TerminalSquare, testid: "nav-scraper" },
  { to: "/impostazioni", label: "Impostazioni", icon: SettingsIcon, testid: "nav-settings" },
];

function Sidebar() {
  const [unread, setUnread] = useState(0);
  useEffect(() => {
    const load = () => api.get("/alerts/count").then((r) => setUnread(r.data.unread)).catch(() => {});
    load();
    const id = setInterval(load, 12000);
    return () => clearInterval(id);
  }, []);
  return (
    <aside className="fixed left-0 top-0 h-full w-64 bg-[#0E1015] border-r border-white/10 flex flex-col z-30">
      <div className="h-16 flex items-center gap-3 px-5 border-b border-white/10">
        <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-amber-400 to-amber-700 flex items-center justify-center copper-glow">
          <ChefHat className="w-5 h-5 text-white" />
        </div>
        <div className="leading-tight">
          <div className="font-head font-extrabold text-[15px] tracking-tight">KüchenChef <span className="text-amber-500">DE</span></div>
          <div className="text-[10px] uppercase tracking-widest text-gray-500">Job Hunter Germania</div>
        </div>
      </div>
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {NAV.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.end}
            data-testid={n.testid}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all ${
                isActive
                  ? "bg-amber-500/15 text-amber-400 border border-amber-500/30"
                  : "text-gray-400 hover:text-gray-100 hover:bg-white/5 border border-transparent"
              }`
            }
          >
            <n.icon className="w-[18px] h-[18px]" />
            <span className="flex-1">{n.label}</span>
            {n.badge && unread > 0 && (
              <span data-testid="alerts-badge" className="min-w-[20px] h-5 px-1.5 rounded-full bg-amber-500 text-black text-[11px] font-bold flex items-center justify-center pulse-glow">
                {unread > 99 ? "99+" : unread}
              </span>
            )}
          </NavLink>
        ))}
      </nav>
      <div className="p-4 border-t border-white/10">
        <div className="text-[10px] text-gray-600 leading-relaxed">
          Scraping etico via pagine Impressum/Kontakt. Aggiornamento automatico ogni 2 ore.
        </div>
      </div>
    </aside>
  );
}

function Shell({ children }) {
  const loc = useLocation();
  return (
    <div className="min-h-screen bg-[#0B0D10]">
      <Sidebar />
      <main className="ml-64 min-h-screen" key={loc.pathname}>
        {children}
      </main>
    </div>
  );
}

function App() {
  return (
    <div className="App">
      <Toaster theme="dark" position="top-right" richColors />
      <BrowserRouter>
        <Shell>
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/hotel" element={<Leads />} />
            <Route path="/offerte" element={<Jobs />} />
            <Route path="/facebook" element={<FacebookImport />} />
            <Route path="/avvisi" element={<Alerts />} />
            <Route path="/cv" element={<CVManager />} />
            <Route path="/invia" element={<Compose />} />
            <Route path="/storico" element={<SentHistory />} />
            <Route path="/scraper" element={<ScraperLogs />} />
            <Route path="/impostazioni" element={<Settings />} />
          </Routes>
        </Shell>
      </BrowserRouter>
    </div>
  );
}

export default App;
